import { ResponseError } from '~/domain/ResponseError';
import { getNonce, raiseLogoutEvent } from '~/store/auth';

const getOptions = (url: string, method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'): RequestInit => {
  const nonce = getNonce();

  const headers: { [key: string]: string } = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    ...(nonce && { 'X-CSRF-TOKEN': nonce}),
  };

  const options: RequestInit = {
    method,
    headers,
  };
  if (url.match(/^http/)) {
    options.mode = 'cors';
  }

  return options;
};

const processResponse =  async <T>(response: Response) => {
  const text = await response.text();
  if (response.status === 401 || response.status === 404) { //Unauthorized
    raiseLogoutEvent();
  }
  if (!response.ok) {
    throw new ResponseError(response.status, response.statusText, text);
  }
  if (text) {
    const result: T = JSON.parse(text);
    return result;
  }
}

const request = async <T, TRequest>(options: RequestInit, fullUrl: string, resource: TRequest): Promise<T | undefined> => {

  if (resource) {
    if (resource instanceof FormData) {
      options.body = resource;
    } else {
      options.body = JSON.stringify(resource);
    }
  }

  const response = await fetch(fullUrl, options);
  return processResponse(response);
};

export const GET_IMAGE = async (src: string, options: RequestInit) => {

  if (src.match(/^https/)) {
    options.mode = 'cors';
    options.credentials = 'include';
  }

  const response = await fetch(src, options);

  if (!response.ok) {
    const json = await response.json();
    throw new ResponseError(response.status, json.message, src);
  }

  const blob = await response.blob();
  return URL.createObjectURL(blob);
}

export const GET_WITH_CREDENTIALS = async <TResponse>(fullUrl: string) => {
  const options = getOptions(fullUrl, 'GET');
  if (fullUrl.match(/^https/)) {
    options.credentials = 'include';
  }

  const response = await fetch(fullUrl, options);
  return processResponse<TResponse>(response);
}

export const GET = async <TResponse>(fullUrl: string) => {
  return await request.call(this, getOptions(fullUrl, 'GET'), fullUrl, null) as TResponse;
};

export const POST = async <TResponse, TRequest>(fullUrl: string, resource: TRequest) => {
  return await request.call(this, getOptions(fullUrl, 'POST'), fullUrl, resource) as TResponse;
};

export const POST_WITH_CREDENTIALS = async <TResponse, TRequest>(fullUrl: string, resource: TRequest) => {
  const options = getOptions(fullUrl, 'POST');
  if (fullUrl.match(/^https/)) {
    options.credentials = 'include';
  }
  return await request.call(this, options, fullUrl, resource) as TResponse;
}

export const PUT = async <TResponse, TRequest>(fullUrl: string, resource: TRequest) => {
  return await request.call(this, getOptions(fullUrl, 'PUT'), fullUrl, resource) as TResponse;
};

export const PUT_WITH_CREDENTIALS = async <TResponse, TRequest>(fullUrl: string, resource: TRequest) => {
  const options = getOptions(fullUrl, 'PUT');
  if (fullUrl.match(/^https/)) {
    options.credentials = 'include';
  }
  return await request.call(this, options, fullUrl, resource) as TResponse;
};

export const DELETE = async <TResponse>(fullUrl: string, resource?: string[]) => {
  return await request.call(this, getOptions(fullUrl, 'DELETE'), fullUrl, resource) as TResponse;
};

export const PATCH = async <TResponse, TRequest>(fullUrl: string, resource: TRequest) => {
  return await request.call(this, getOptions(fullUrl, 'PATCH'), fullUrl, resource) as TResponse;
};

export const UPLOAD = async <TResponse>(fullUrl: string, resource: FormData) => {
  const options = getOptions(fullUrl, 'POST');
  const nonce = getNonce();
  options.headers = {
    'Accept': 'application/json',
    ...(nonce && { 'X-CSRF-TOKEN': nonce}),
  };
  return await request.call(this, options, fullUrl, resource) as TResponse;
};

export const UPLOAD_WITH_CREDENTIALS = async <TResponse>(fullUrl: string, resource: FormData) => {
  const options = getOptions(fullUrl, 'POST');
  const nonce = getNonce();
  options.headers = {
    'Accept': 'application/json',
    ...(nonce && { 'X-CSRF-TOKEN': nonce}),
  };
  if (fullUrl.match(/^https/)) {
    options.credentials = 'include';
  }
  return await request.call(this, options, fullUrl, resource) as TResponse;
};
